import { useState } from "react";
import { Layout, Menu, Avatar, Dropdown, Button, Badge, Modal, Input, Select } from "antd";
import {
  LayoutDashboard,
  Building2,
  Package,
  Factory,
  ShieldCheck,
  Users,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Bell,
  Activity,
  Layers,
  Flag,
  Smartphone,
  Cpu,
  Download,
  RefreshCw,
  AlertTriangle,
  BadgeCheck,
  Car,
  Boxes,
  Wrench,
  Settings2,
  BriefcaseBusiness,
  Receipt,
  ClipboardList,
  ShoppingCart,
  Scale,
  Users2,
  FileText,
  Plug,
  ClipboardCheck,
  CreditCard,
  Megaphone,
  ContactRound,
  MapPin,
  ShieldAlert,
  Rocket,
  GitBranch,
} from "lucide-react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { authApi } from "../../api/auth";
import { useAuthStore } from "../../store/useAuthStore";
import { useCompanyList } from "../../api-hooks/companies";

const { Sider, Header, Content } = Layout;

const PER_COMPANY_ROUTES = ["/inventory", "/fleet", "/job-config", "/company-fields", "/company-config", "/jobs", "/invoices", "/quotations", "/purchase-orders", "/compliance", "/hr", "/documents", "/integrations", "/pqq", "/billing", "/clients", "/employee-locations"];

function icon(I: any) {
  return <I size={15} />;
}
function grpLabel(text: string) {
  return (
    <span
      style={{
        color: "#7c5cbf",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
      }}
    >
      {text}
    </span>
  );
}

function buildItems(handleNav: (k: string) => void) {
  const item = (key: string, label: string, I: any) => ({
    key,
    icon: icon(I),
    label,
    onClick: () => handleNav(key),
  });

  return [
    { key: "g-platform", type: "group" as const, label: grpLabel("Platform"), children: [
      item("/dashboard", "Dashboard", LayoutDashboard),
      item("/companies", "Companies", Building2),
      item("/plans", "Plans", Package),
      item("/industries", "Industries", Factory),
      item("/platform-fleet", "Fleet Overview", Car),
      item("/platform-compliance", "Compliance Risk", ShieldAlert),
      item("/onboarding-overview", "Onboarding Progress", Rocket),
      item("/pipeline-overview", "Pipeline Config", GitBranch),
    ]},
    { key: "g-access", type: "group" as const, label: grpLabel("Access Control"), children: [
      item("/roles", "Roles & RBAC", ShieldCheck),
      item("/admins", "Admin Users", Users),
      item("/certifications", "Certifications", BadgeCheck),
    ]},
    { key: "g-tools", type: "group" as const, label: grpLabel("Platform Tools"), children: [
      item("/activity", "Activity Log", Activity),
      item("/custom-fields", "Custom Fields", Layers),
      item("/feature-flags", "Feature Flags", Flag),
      item("/app-versions", "App Versions", Smartphone),
      item("/background-jobs", "Background Jobs", Cpu),
      item("/data-exports", "Data Exports", Download),
      item("/sync-queue", "Sync Queue", RefreshCw),
      item("/rate-limit-logs", "Rate Limits", AlertTriangle),
      item("/push-tokens", "Push Tokens", Bell),
      item("/announcements", "Announcements", Megaphone),
    ]},
    { key: "g-percompany", type: "group" as const, label: grpLabel("Per Company"), children: [
      item("/company-config", "Company Config", Settings2),
      item("/jobs", "Jobs", BriefcaseBusiness),
      item("/invoices", "Invoices", Receipt),
      item("/quotations", "Quotations", ClipboardList),
      item("/purchase-orders", "Purchase Orders", ShoppingCart),
      item("/compliance", "UK Compliance", Scale),
      item("/hr", "HR & Payroll", Users2),
      item("/documents", "Documents", FileText),
      item("/integrations", "Integrations", Plug),
      item("/pqq", "PQQ", ClipboardCheck),
      item("/billing", "Billing", CreditCard),
      item("/inventory", "Inventory", Boxes),
      item("/fleet", "Fleet", Car),
      item("/job-config", "Job Config", Wrench),
      item("/clients", "Clients", ContactRound),
      item("/employee-locations", "Employee Locations", MapPin),
      item("/company-fields", "Custom Fields", Settings2),
    ]},
  ];
}

export default function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [companyPickerTarget, setCompanyPickerTarget] = useState<string | null>(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerCompanyId, setPickerCompanyId] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, clearAuth } = useAuthStore();

  const { data: pickerCompanies = [] } = useCompanyList({ key: pickerSearch || undefined, limit: 50 });

  const handleNavClick = (key: string) => {
    if (PER_COMPANY_ROUTES.includes(key)) {
      setCompanyPickerTarget(key);
      setPickerSearch("");
      setPickerCompanyId(null);
    } else {
      navigate(key);
    }
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {}
    clearAuth();
    window.location.href = "/";
  };

  const userMenu = {
    items: [
      {
        key: "profile",
        label: `Signed in as ${admin?.name || "Admin"}`,
        disabled: true,
      },
      { type: "divider" as const },
      {
        key: "logout",
        label: "Sign Out",
        icon: <LogOut size={14} />,
        danger: true,
        onClick: handleLogout,
      },
    ],
  };

  const selectedKey = (() => {
    for (const seg of ["/jobs/", "/invoices/", "/quotations/", "/purchase-orders/", "/compliance/", "/hr/", "/documents/", "/integrations/", "/pqq/", "/billing/", "/inventory/", "/fleet/", "/job-config/", "/clients/", "/employee-locations/", "/company-fields/", "/company-config/"]) {
      if (location.pathname.startsWith(seg)) return seg.replace(/\/$/, "");
    }
    // Exact platform-level routes
    if (location.pathname === "/platform-fleet") return "/platform-fleet";
    if (location.pathname === "/platform-compliance") return "/platform-compliance";
    if (location.pathname === "/onboarding-overview") return "/onboarding-overview";
    if (location.pathname === "/pipeline-overview") return "/pipeline-overview";
    return location.pathname;
  })();

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        collapsed={collapsed}
        width={220}
        collapsedWidth={60}
        style={{ background: "#1e1048", position: "fixed", height: "100vh", left: 0, top: 0, bottom: 0, zIndex: 100 }}
      >
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Logo */}
          <div className="flex items-center gap-3 px-4 py-4 border-b border-violet-800/50 flex-shrink-0">
            <div className="w-10 h-10">
  <img
    src="/sitepro_logo.png"
    alt="SitePro Logo"
    className="w-full object-contain h-full"
  />
</div>
            {!collapsed && (
              <div>
                <div className="text-white font-semibold text-sm leading-tight">Platform Admin</div>
                <div className="text-violet-400 text-[10px]">Onap Sitepro</div>
              </div>
            )}
          </div>

          {/* Scrollable Nav */}
          <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", scrollbarWidth: "none" }}>
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[selectedKey]}
              style={{ background: "transparent", border: "none", paddingBottom: 48 }}
              items={buildItems(handleNavClick)}
            />
          </div>
        </div>

        {/* Collapse toggle */}
        <div
          style={{
            position: "absolute",
            bottom: 12,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            type="text"
            icon={
              collapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />
            }
            onClick={() => setCollapsed(!collapsed)}
            style={{ color: "#a78bfa" }}
            size="small"
          />
        </div>
      </Sider>

      <Layout
        style={{
          marginLeft: collapsed ? 60 : 220,
          transition: "margin-left 0.2s",
        }}
      >
        <Header
          style={{
            background: "#fff",
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #f0f0f0",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <div className="text-slate-700 font-medium text-sm">
            {location.pathname
              .replace(/^\//, "")
              .split("/")[0]
              .replace(/-/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase()) || "Platform Admin"}
          </div>
          <div className="flex items-center gap-3">
            <Badge count={0} showZero={false}>
              <Button type="text" icon={<Bell size={16} />} />
            </Badge>
            <Dropdown menu={userMenu} placement="bottomRight">
              <div className="flex items-center gap-2 cursor-pointer px-2 py-1 rounded-lg hover:bg-violet-50 transition-colors">
                <Avatar
                  size={30}
                  style={{
                    background: "#4c1d95",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {(admin?.name || "A")[0].toUpperCase()}
                </Avatar>
                {!collapsed && (
                  <div className="hidden sm:block">
                    <div className="text-slate-800 text-xs font-medium leading-tight">
                      {admin?.name}
                    </div>
                    <div className="text-violet-600 text-[10px] capitalize">
                      {admin?.role?.replace("_", " ")}
                    </div>
                  </div>
                )}
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content
          style={{
            padding: "24px",
            background: "#f8f7ff",
            minHeight: "calc(100vh - 64px)",
          }}
        >
          <Outlet />
        </Content>
      </Layout>

      <Modal
        title="Select Company"
        open={!!companyPickerTarget}
        onCancel={() => { setCompanyPickerTarget(null); setPickerCompanyId(null); }}
        onOk={() => {
          if (pickerCompanyId) {
            navigate(`${companyPickerTarget}/${pickerCompanyId}`);
            setCompanyPickerTarget(null);
            setPickerCompanyId(null);
          }
        }}
        okText="Open"
        okButtonProps={{ disabled: !pickerCompanyId, style: { background: "#4c1d95", borderColor: "#4c1d95" } }}
      >
        <p className="text-slate-500 text-sm mb-3">
          Choose a company to manage <strong>{companyPickerTarget?.replace(/\//g, "").replace(/-/g, " ")}</strong> for.
        </p>
        <Select
          showSearch
          style={{ width: "100%" }}
          placeholder="Search company name..."
          filterOption={false}
          value={pickerCompanyId ?? undefined}
          onSearch={(v) => setPickerSearch(v)}
          onChange={(v) => setPickerCompanyId(v)}
          options={pickerCompanies.map((c: any) => ({
            value: c.id,
            label: `#${c.id} — ${c.name} (${c.email})`,
          }))}
          autoFocus
          allowClear
        />
      </Modal>
    </Layout>
  );
}
